import {assert} from 'Asserts';
import {InventoryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import PrintBarcodeModal from 'label-printing/PrintBarcodeModal';
import legacyRunModal from 'ui-library/legacyRunModal';

import getBarcodePrintableIdsInSelection, {
  SELECT_ITEMS_TO_PRINT_TOOLTIP_TEXT,
} from '../components/getBarcodePrintableIdsInSelection';
import {ActionFunctionParameters} from '../StructuredTableConstants';
import makeSimpleActionFunction from './makeSimpleActionFunction';

/**
 * An ActionFunction that lets the user print barcodes. The user must select one or more
 * cells with printable barcodes (e.g., containers or plates), or the button will be disabled.
 */
export const printBarcodesActionFunction = makeSimpleActionFunction({
  label: 'Print labels',
  icon: 'format-print',
  onInvoke: ({structuredTableAdapter, structuredTable, computedTable, multiSelectionBox}) => {
    assert(
      multiSelectionBox != null,
      'Expecting button to be disabled when there is no selection, but click handler was called'
    );

    const table = structuredTableAdapter.toTable(structuredTable);
    legacyRunModal(PrintBarcodeModal, {
      itemIds: getBarcodePrintableIdsInSelection(
        multiSelectionBox,
        structuredTable.columns,
        computedTable,
        table.rowData ?? [],
        structuredTableAdapter.kind
      ).printableIds,
    });
  },
  getTooltip: getPrintButtonTooltip,
  getDisabled: hasPrintableSelection,
  getHidden: () => !isFeatureEnabled({feature: InventoryFeature.INVENTORY_EXPORT}),
});

function hasPrintableSelection({
  structuredTableAdapter,
  structuredTable,
  computedTable,
  multiSelectionBox,
}: ActionFunctionParameters<unknown, unknown>): boolean {
  const table = structuredTableAdapter.toTable(structuredTable);
  return (
    multiSelectionBox == null ||
    getBarcodePrintableIdsInSelection(
      multiSelectionBox,
      structuredTable.columns,
      computedTable,
      table.rowData ?? [],
      structuredTableAdapter.kind
    ).printableIds.length === 0
  );
}

function getPrintButtonTooltip({
  structuredTableAdapter,
  structuredTable,
  computedTable,
  multiSelectionBox,
}: ActionFunctionParameters<unknown, unknown>): string | null {
  if (!multiSelectionBox) {
    return SELECT_ITEMS_TO_PRINT_TOOLTIP_TEXT;
  }
  const table = structuredTableAdapter.toTable(structuredTable);
  return getBarcodePrintableIdsInSelection(
    multiSelectionBox,
    structuredTable.columns,
    computedTable,
    table.rowData ?? [],
    structuredTableAdapter.kind
  ).tooltip;
}
